/**
 * The BYOK provider catalog -- shared between the backend (`services/aiProvider.service.ts`,
 * which does the actual key validation and model construction) and every settings UI that lets a
 * workspace manage its own key (`AiProviderKeysSection.tsx`). Neither side redeclares this list;
 * a fifth provider is a one-line addition here, not a hunt through both packages.
 */
export const AI_PROVIDERS = ['anthropic', 'openai', 'google', 'openrouter'] as const;
export type AiProvider = (typeof AI_PROVIDERS)[number];

export const AI_PROVIDER_LABELS: Record<AiProvider, string> = {
  anthropic: 'Anthropic (Claude)',
  openai: 'OpenAI',
  google: 'Google (Gemini)',
  openrouter: 'OpenRouter',
};

/**
 * BYOK default model per provider, matched to the *tier* of the hosted default
 * (gemini-3.1-flash-lite: fast, cheap, good enough for page-aware chat and email analysis) --
 * not each provider's flagship. A workspace paying its own bill still shouldn't be defaulted into
 * the most expensive model available; that's an opt-in, not a default.
 *
 * `google` reuses the exact hosted model string since it's already verified working in this
 * codebase. The other three are the best-effort equivalent: single named constants specifically
 * so they're a one-line change -- verify each against the provider's current catalog before
 * launch, since a wrong or retired model ID fails at call time, not at key-validation time.
 */
export const AI_PROVIDER_DEFAULT_MODEL: Record<AiProvider, string> = {
  anthropic: 'claude-haiku-4-5-20251001',
  openai: 'gpt-5-mini',
  google: 'gemini-3.1-flash-lite',
  openrouter: 'anthropic/claude-haiku-4.5',
};

/**
 * The hosted model every workspace gets by default (no BYOK key configured), and its published
 * per-token rate. Single source of truth for both the cost estimate in
 * `trpc/routers/billing.ts` and the "what am I paying for" card in `AiProviderKeysSection.tsx` --
 * a rate change is a one-line edit here, not two files that can drift apart.
 */
export const HOSTED_MODEL_LABEL = 'Gemini 3.1 Flash Lite';
export const HOSTED_MODEL_PRICING = {
  inputPerMillionUsd: 0.075,
  outputPerMillionUsd: 0.3,
} as const;
